import { useEffect, useState, type RefObject } from "react";

/** The nearest ancestor that scrolls vertically (null: the page itself). */
function scrollParent(el: Element): Element | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if (overflowY === "auto" || overflowY === "scroll") return p;
  }
  return null;
}

/**
 * True once the element has come within `rootMargin` of what its scroll container shows, and from then on (it
 * latches: a map scrolled past stays drawn, so scrolling back shows it at once). The margin is measured on the
 * scroll container, not the window: the window's margin would not reach past the container's clipped edge. Where
 * IntersectionObserver is missing, true straight away.
 */
export function useNearViewport(ref: RefObject<Element | null>, rootMargin: string): boolean {
  const [near, setNear] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const el = ref.current;
    if (near || !el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { root: scrollParent(el), rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin, near]);
  return near;
}
